import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FilterMatchMode } from "primereact/api";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Actions } from "../../components/datatable/Actions";
import { FormSkeleton, Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useAgents } from "../../hooks/useAgents";
import { AgentObject } from "../../interfaces";
import { AppContext } from "../../context/index";
import { Button, TabView } from "iuvity-ui-components";
import { Controller, useForm, useWatch } from "react-hook-form";
import { MultiSelect } from "primereact/multiselect";
import { Column } from 'primereact/column'
import { Dropdown } from 'primereact/dropdown'
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { RemovedAgents } from "../../components/agents/RemovedAgents";
import { AgentsFormSchema } from "../../validations/schemas";
import { Tag } from "iuvity-ui-components/dist/tag";
import { capitalizeFirstLetter } from "../../utils";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

const formBlankValues = {
  name: '',
  version: '',
  url: '',
  status: ''
};

export const AdminAgentsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { user, language, pageFilter, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: pageFilter
      ? {
          name: pageFilter?.name || '',
          version: pageFilter?.version || '',
          url: pageFilter?.url || '',
          status: pageFilter?.status || '',
        }
      : formBlankValues,
  });

  const { control, setValue } = form;
  const nameValues = useWatch({
    control,
    name: "name",
  });

  const versionValues = useWatch({
    control,
    name: "version",
  });

  const urlValues = useWatch({
    control,
    name: "url",
  });

  const statusValues = useWatch({
    control,
    name: "status",
  });

  const { data, agentsAction, reloadFn } = useAgents("DATA");
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const columns = useMemo(() => {
    if (language) {
      return [
        {
          field: "name",
          header: t("Agents.nameLabel"),
        },
        {
          field: "version",
          header: t("Agents.versionLabel"),
        },
        {
          field: "url",
          header: t("Agents.urlLabel"),
        },
        {
          field: "status",
          header: t("Agents.statusLabel"),
        },
        {
          field: "actions",
          header: t("Agents.actionsLabel"),
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    version: { value: null, matchMode: FilterMatchMode.CONTAINS },
    url: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  };

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState(defaultFilterValues);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const actionEditFn = (agent: AgentObject) => {
    setAppState((prev) => ({
      ...prev,
      selectedAgent: agent,
    }));
    navigate(`${location.pathname}/edit`);
    window.scrollTo(0, 0);
  };

  const actionDeleteFn = async (agent: AgentObject) => {
    await agentsAction("DELETE", agent.id);
    reloadFn();
  };

  const handleModalProps = async (agent: AgentObject) => {
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: t("Agents.deleteModalTitle"),
      content: `${t("Agents.deleteModalContent1")} "${
        agent?.name
      }" ${t("Agents.deleteModalContent2")}`,
      onConfirm: () => actionDeleteFn(agent),
    }));
  };

  const onClearFilters = () => {
    setFilters(defaultFilterValues);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(formBlankValues);
  };

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`Agents.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  }
  const filterTemplate = (options: any) => {
    const arrayOriginal = data
      ?.filter((agent: any) => agent.status === 'ACTIVE' || agent.status === 'DRAFT' || agent.status === 'INACTIVE')
      ?.map((agent: any) => ({label: agent[`${options.field}`], value: agent[`${options.field}`]}))
      ?.sort((a: any, b: any) => a.label.localeCompare(b.label));
    
    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) => 
        duplicateObject.label === object.label) === currentIndex;
    });
    
    return (
      <Controller
          name={options.field}
          control={control}
          rules={(AgentsFormSchema(t) as any)[options.field]}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
              options.filterCallback(e.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-1">
                  {t(`Agents.${options.field}Label`)}
                </label>
                <MultiSelect
                  display="chip"
                  options={arrayWithoutDuplicates}
                  optionLabel={'label'}
                  itemTemplate={ItemsTemplate}
                  placeholder={t(`Agents.${options.field}PlaceholderLabel`)} 
                  className="mb-2"
                  emptyFilterMessage={t("Agents.noAvailableMessage")}
                  filter
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const statuses = [
    `${t("Agents.activeLabel")}`,
    `${t("Agents.inactiveLabel")}`,
    `${t("Agents.draftLabel")}`,
  ];
  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  };
  const statusFilterTemplate = (options: any) => {
    return (
      <Controller
            name={options.field}
            control={control}
            render={({ field }) => {
              field.onChange = (e) => {
                setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
                options.filterCallback(e.value);
              };
              return (
                <>
                  <label htmlFor={options.field} className="block lb mt-2 mb-3">
                    {t(`Agents.${options.field}Label`)}
                  </label>
                  {/* // @ts-ignore */}
                  <Dropdown 
                    options={statuses}
                    itemTemplate={statusItemTemplate}
                    placeholder={t(`Agents.${options.field}PlaceholderLabel`)}
                    className="p-column-filter mb-2"
                    showClear={false}
                    {...field} 
                    />
                </>
              );
            }}
          />
      
    )
  };

  const filterClearTemplate = (options: any) => {
    return (
      <Button
        label={t("Agents.clearFilterLabel")}
        variant="p-button-text"
        onClick={onClearFilters}
      />
    )
  };
  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        label={t("Agents.applyFilterLabel")}
        className="p-button"
        disabled={
          !(
            nameValues.length > 0 ||
            versionValues.length > 0 ||
            urlValues.length > 0 ||
            statusValues.length > 0
            )
        }
        onClick={options.filterApplyCallback}
      />
    )
  };

  const statusBodyTemplate = (status: any) => (
    <Tag
      value={capitalizeFirstLetter(status)}
      severity={
        (['active', 'activo', 'activa'].includes(status.toLowerCase()) && 'success') ||
        (['draft', 'borrador'].includes(status.toLowerCase()) && 'info') ||
        (['delete', 'eliminado'].includes(status.toLowerCase()) && 'danger') ||
        'warning'
      }
    />
  );

  const allFieldsBodyTemplate = (rowData:any, field: string, criteria: string) => {
    return (
      <div
      style={{
        boxShadow : (!rowData[criteria] && field !== 'actions')
          ? 'var(--backgroundColor) 0px 0px 0px 500px'
          : 'none',
        background: (!rowData[criteria] && field !== 'actions')
          ? 'var(--backgroundColor)'
          : 'none',
      }}
    >
      {(field === "status") ? statusBodyTemplate(rowData[field]) : rowData[field]}
    </div>
    )
  };

  useEffect(() => {
    if (sessionStorage.getItem('filters-table')) {
      const dataSession = JSON.parse(sessionStorage.getItem('filters-table') || "");
      const global = dataSession.filters.global;
      setGlobalFilterValue(global);
    }
  }, [filters]);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div>
            {t("Agents.agentsTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Agents.deleteThisTranslate"),
              disabled: true
            },
            {
              label: t("Agents.agentsDatatableTitle"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content">
          <span>{t("Agents.agentsFirstDescription")}</span>
        </div>
      </TransactionalSubheader>

      <BoxFull className="my-4 px-0 py-0" variant="tertiary">
        <TabView
          className="upload-tabview mt-4"
          activeIndex={activeIndex}
          onTabChange={(e) => {
            setGlobalFilterValue("");
            sessionStorage.removeItem('filters-table');
            setFilters(defaultFilterValues);
            setActiveIndex(e.index);
            if (e.index === 0 && form.formState.isDirty) form.reset();
          }}
          items={[
            {
              key: "1",
              header: t("Agents.availableAgents"),
              children: (
                <>
                    <BoxFull className="mt-0 mx-0 px-0 pb-3" variant="tertiary">
                      <div className="subheader-buttons">
                        <SubheaderActions
                          className="my-0 mx-0 px-0 py-0"
                          actions={subheaderActionsPrivilegeValidator([
                            {
                              id: "111",
                              icon: "iu iu-icon-plus",
                              label: t("Agents.newAgentTitle"),
                              style: {
                                display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
                              },
                              onClick: () => navigate(`${location.pathname}/new`),
                            },
                          ])}
                        />
                      </div>
                    </BoxFull>
                  <BoxFull className="dt-c my-0 mx-0">
                  {data ? (
                    <DataTable
                      header={
                        <TableHeader
                          title={t("Agents.availableAgents")}
                          columns={columns}
                          selectedColumns={selectedColumns}
                          toggleColumns={setSelectedColumns}
                          filters={filters}
                          setFilters={setFilters}
                          reload={reloadFn}
                          onClearFiltersColumn={onClearFilters}
                          globalFilterValue={globalFilterValue}
                        />
                      }
                      currentPageReportTemplate={t(
                        "ConsoleTranslations.rowsPaginatorTemplateLabel"
                      )}
                      paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
                      emptyMessage={t("TableActions.emptyMessage")}
                      rows={10}
                      size="normal"
                      editMode="row"
                      dataKey="id"
                      sortField="functionality"
                      sortOrder={1}
                      filters={filters}
                      value={(data?.filter((tenant: any) => tenant?.status !== 'DELETED').map((agent: AgentObject) => ({
                        ...agent,
                        status: t(`Agents.${agent?.status?.toLowerCase()}Label`),
                        actions: (
                          <Actions
                            enableDelete={!!agent.createdby}
                            onEdit={() => actionEditFn(agent)}
                            onDelete={() => handleModalProps(agent)}
                            visibleObject={{...permissiondInfoByRole}}
                          />
                        ),
                      })))}
                      scrollable
                      resizableColumns
                      globalFilterFields={['name', 'version', 'url', 'status']}
                      filterDisplay="menu"
                      stateStorage="session"
                      stateKey="filters-table"
                    >
                      {selectedColumns.map((col) => (
                        <Column
                          key={col.field}
                          field={col.field}
                          header={col.header}
                          sortable={!(col.field === "actions")}
                          align={col.field === "actions" ? "center" : "left"}
                          headerStyle={col.field === "actions"
                            ? {
                                justifyContent: "center",
                                display: (!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined,
                              }
                            : undefined }
                          style={{
                            boxShadow: (col.field === "actions")
                              ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                              : undefined,
                            maxWidth: (col.field === "actions") ? "132px" : undefined,
                            whiteSpace: "normal",
                            minWidth: (col.field === "actions") ? "8rem" : "10rem",
                            display: (col.field === "actions")
                              ? ((!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined)
                              : undefined,
                          }}
                          frozen={col.field === "actions" ? true : false}
                          alignFrozen={col.field === "actions" ? "right" : undefined}
                          filterField={col.field}
                          showFilterMatchModes={false}
                          filterMenuStyle={{ width: '23rem'}}
                          filter={col.field === "actions" ? false : true}
                          filterElement={
                            ((col.field === "name" || col.field === "version" || col.field === "url")  && filterTemplate) ||
                            (col.field === "status" && statusFilterTemplate)
                          }
                          filterApply={filterApplyTemplate}
                          filterClear={filterClearTemplate}
                          body={(rowData) => allFieldsBodyTemplate(rowData, col.field, 'createdby')}
                        />
                      ))}
                    </DataTable>
                  ) : (
                    <FormSkeleton length={8} />
                  )}
                  </BoxFull>
                </>
              ),
              disabled: false,
            },
            {
              key: "2",
              header: t("Agents.removedAgents"),
              children: (
                <RemovedAgents
                  title={t("Agents.removedAgents")}
                  data={data}
                  actionFn={agentsAction}
                  reloadFn={reloadFn}
                />
              ),
            },
          ]}
        />
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
